import produce from 'immer';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { VariableType } from '@mode-vegas/vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { DomainAppState } from '../charts-domain.types';
import { withLatestSnapshot } from '../helpers';
import * as ChartActions from '../chart/chart.actions';
import { FieldFormatHelpers } from '@mode/charts/util-vegas';
import * as ChartSwitchActions from '../chart/chart-switch.actions';

interface PatchOptions<T extends ChartTypes.FieldFormatTypes> {
  formatKey: T;
  format: ChartTypes.FieldFormat[T];
  snapshot: ChartTypes.ChartSnapshot;
  varType: VariableType;
  formula: string;
  token: string;
  isSwitchView: boolean;
  type: string;
}

function createFieldFormats<T extends ChartTypes.FieldFormatTypes>({
  snapshot,
  formula,
  varType,
  format,
  formatKey,
}: PatchOptions<T>) {
  return produce(snapshot.fieldFormats || {}, (draft) => {
    const fieldFormatKey = FieldFormatHelpers.getFormatMapKey(formula, varType);
    const fieldFormat = (draft[fieldFormatKey] ||= FieldFormatHelpers.getDefaultFieldFormat());
    fieldFormat[formatKey] = {
      ...fieldFormat[formatKey],
      ...format,
    };
  });
}

@Injectable()
export class FormatEffects {
  constructor(
    private store: Store<DomainAppState>,
    private actions$: Actions
  ) {}

  updateAxisFieldFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateAxisFieldFormat, ChartSwitchActions.updateAxisFieldFormat),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return FormatEffects.patchFieldFormats({
          formatKey: ChartTypes.FieldFormatTypes.Axis,
          ...action,
          snapshot,
          token,
          isSwitchView: action.type === ChartSwitchActions.updateAxisFieldFormat.type,
        });
      })
    )
  );

  updateBaseFieldFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateBaseFormatDisplayName, ChartSwitchActions.updateBaseFormatDisplayName),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return FormatEffects.patchFieldFormats({
          formatKey: ChartTypes.FieldFormatTypes.Base,
          ...action,
          snapshot,
          token,
          isSwitchView: action.type === ChartSwitchActions.updateBaseFormatDisplayName.type,
        });
      })
    )
  );

  updateHeaderFieldFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateHeaderFieldFormat, ChartSwitchActions.updateHeaderFieldFormat),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return FormatEffects.patchFieldFormats({
          formatKey: ChartTypes.FieldFormatTypes.Header,
          ...action,
          snapshot,
          token,
          isSwitchView: action.type === ChartSwitchActions.updateHeaderFieldFormat.type,
        });
      })
    )
  );

  updateLegendFieldFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateLegendFieldFormat, ChartSwitchActions.updateLegendFieldFormat),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return FormatEffects.patchFieldFormats({
          formatKey: ChartTypes.FieldFormatTypes.Legend,
          ...action,
          snapshot,
          token,
          isSwitchView: action.type === ChartSwitchActions.updateLegendFieldFormat.type,
        });
      })
    )
  );

  updatePaneFieldFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updatePaneFieldFormat, ChartSwitchActions.updatePaneFieldFormat),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return FormatEffects.patchFieldFormats({
          formatKey: ChartTypes.FieldFormatTypes.Pane,
          ...action,
          snapshot,
          token,
          isSwitchView: action.type === ChartSwitchActions.updatePaneFieldFormat.type,
        });
      })
    )
  );

  private static patchFieldFormatsForSwitchViewChart<T extends ChartTypes.FieldFormatTypes>(options: PatchOptions<T>) {
    const { token, snapshot, type } = options;
    const chart = { ...snapshot, fieldFormats: createFieldFormats(options) };

    return ChartSwitchActions.updateChartNoEncoding({
      token,
      chart,
      sourceAction: type,
    });
  }

  private static patchFieldFormatsForQueryChart<T extends ChartTypes.FieldFormatTypes>(options: PatchOptions<T>) {
    const { token, formatKey, snapshot } = options;
    const chart = { ...snapshot, fieldFormats: createFieldFormats(options) };

    return ChartActions.updateChartNoEncoding({
      token,
      chart,
      description: `update field ${formatKey}`,
    });
  }

  private static patchFieldFormats<T extends ChartTypes.FieldFormatTypes>(options: PatchOptions<T>) {
    if (options.isSwitchView) {
      return this.patchFieldFormatsForSwitchViewChart(options);
    } else {
      return this.patchFieldFormatsForQueryChart(options);
    }
  }
}
